import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MessageSquare, Check, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WidgetContact() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const theme = searchParams.get('theme') || 'dark';
  const isLight = theme === 'light';
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_date: '',
    event_type: '',
    message: ''
  });

  // Track analytics on mount
  useEffect(() => {
    const trackView = async () => {
      try {
        let sessionId = sessionStorage.getItem('analytics_session');
        if (!sessionId) {
          sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('analytics_session', sessionId);
        }
        await axios.post(`${API}/analytics/track/${userId}`, {
          event_type: 'widget_view',
          page: 'widget_contact',
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          screen_size: `${window.innerWidth}x${window.innerHeight}`,
          session_id: sessionId
        });
      } catch (e) { /* Silent fail */ }
    };
    if (userId) trackView();
  }, [userId]);

  // Send resize message to parent for iframe auto-height
  useEffect(() => {
    const sendHeight = () => {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: 'djcrm-resize', widget: 'contact', height }, '*');
    };
    
    sendHeight();
    const observer = new ResizeObserver(sendHeight);
    observer.observe(document.body);
    window.addEventListener('resize', sendHeight);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', sendHeight);
    };
  }, [submitted]);

  // Set body background color for proper iframe display
  useEffect(() => {
    document.body.style.backgroundColor = isLight ? '#ffffff' : '#0a0a0f';
    document.documentElement.style.backgroundColor = isLight ? '#ffffff' : '#0a0a0f';
    return () => {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, [isLight]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.event_date) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/public/contact/${userId}`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (submitted) {
    return (
      <div className={`min-h-fit py-6 flex items-center justify-center p-4 ${isLight ? 'bg-white' : 'bg-obsidian'}`}>
        <div className="w-full max-w-md">
          <div className={`rounded-2xl p-8 border text-center ${isLight ? 'bg-gray-50 border-gray-200 shadow-lg' : 'glass-card border-glass-border'}`}>
            <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-6">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <h1 className={`font-heading text-2xl font-bold ${isLight ? 'text-gray-900' : 'text-foreground'}`}>Message Sent!</h1>
            <p className={`mt-2 ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>
              Thank you for reaching out. We'll get back to you as soon as possible.
            </p>
          </div>
          <p className={`text-center text-xs mt-4 ${isLight ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Powered by DJ CRM
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-fit py-6 flex items-center justify-center p-4 ${isLight ? 'bg-white' : 'bg-obsidian'}`}>
      <div className="w-full max-w-md">
        <div className={`rounded-2xl p-8 border ${isLight ? 'bg-gray-50 border-gray-200 shadow-lg' : 'glass-card border-glass-border'}`}>
          <div className="text-center mb-6">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${isLight ? 'bg-purple-100' : 'bg-neon-purple/20'}`}>
              <MessageSquare className={`w-8 h-8 ${isLight ? 'text-purple-600' : 'text-neon-purple'}`} />
            </div>
            <h1 className={`font-heading text-2xl font-bold mt-4 ${isLight ? 'text-gray-900' : 'text-foreground'}`}>Contact Us</h1>
            <p className={`mt-2 ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>Tell us about your event</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className={isLight ? 'text-gray-900' : ''}>Your Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
                required
                className={isLight ? 'bg-white border-gray-300 text-gray-900' : 'bg-muted/20 border-glass-border'}
                data-testid="contact-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className={isLight ? 'text-gray-900' : ''}>Email Address *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                required
                className={isLight ? 'bg-white border-gray-300 text-gray-900' : 'bg-muted/20 border-glass-border'}
                data-testid="contact-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label className={isLight ? 'text-gray-900' : ''}>Phone Number</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className={isLight ? 'bg-white border-gray-300 text-gray-900' : 'bg-muted/20 border-glass-border'}
                data-testid="contact-phone-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isLight ? 'text-gray-900' : ''}>Event Date *</Label>
                <Input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                  min={getMinDate()}
                  required
                  className={isLight ? 'bg-white border-gray-300 text-gray-900' : 'bg-muted/20 border-glass-border'}
                  data-testid="contact-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className={isLight ? 'text-gray-900' : ''}>Event Type</Label>
                <Input
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  placeholder="Wedding"
                  className={isLight ? 'bg-white border-gray-300 text-gray-900' : 'bg-muted/20 border-glass-border'}
                  data-testid="contact-type-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className={isLight ? 'text-gray-900' : ''}>Message</Label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us more about your event..."
                className={`w-full min-h-[100px] rounded-lg p-3 focus:outline-none transition-colors ${
                  isLight 
                    ? 'bg-white border border-gray-300 text-gray-900 focus:border-purple-500' 
                    : 'bg-muted/20 border border-glass-border text-foreground focus:border-neon-purple'
                }`}
                data-testid="contact-message-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting || !formData.name || !formData.email || !formData.event_date}
              className={`w-full font-semibold py-6 ${
                isLight 
                  ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                  : 'bg-neon-purple hover:bg-neon-purple/80 text-white'
              }`}
              data-testid="contact-submit-btn"
            >
              {submitting ? 'Sending...' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </div>
        
        <p className={`text-center text-xs mt-4 ${isLight ? 'text-gray-400' : 'text-muted-foreground'}`}>
          Powered by DJ CRM
        </p>
      </div>
    </div>
  );
}
